import React, { useEffect, useMemo, useState } from 'react';
import OrderHistoryView from './components/OrderHistoryView';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import LinearProgress from '@mui/material/LinearProgress';
import CommentIcon from '@mui/icons-material/Comment';
import { Button, createTheme, ThemeProvider, Tooltip, Typography, Grid, Box, Paper, IconButton, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import { Field, reduxForm } from 'redux-form';
import PreviewIcon from '@mui/icons-material/Preview';
import SearchIcon from '@mui/icons-material/Search';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OrderDetailsShippingViewDetailsModal from './components/OrderDetailsShippingViewDetailsModal';
import OrderRemarksDetailsModel from './components/OrderRemarksDetailsModel';
import { connect } from 'react-redux';
import { showNotification } from 'src/shared/components/Notification';
import renderMuiDateTimePickerField from 'src/shared/components/form/MuiDatePicker';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import renderSelectField from 'src/shared/components/form/Select';
import { safeFormatDateOnly } from 'src/utils/DateUtils';


// Compact Input Field Renderer
const renderCompactField = ({ input, placeholder, type, meta: { touched, error, warning }, disabled, icon: Icon, children }) => (
  <div>
    <div className={`input-group input-group-sm ${touched && error ? 'has-validation' : ''}`}>
      {Icon && (
        <span className="input-group-text bg-light border-end-0 text-muted" style={{ padding: '0.25rem 0.5rem' }}>
          <Icon style={{ fontSize: '1rem' }} />
        </span>
      )}
      <input
        {...input}
        className={`form-control form-control-sm ${touched && error ? 'is-invalid' : ''} ${Icon ? 'border-start-0' : ''}`}
        placeholder={placeholder}
        type={type}
        disabled={disabled}
        style={{ fontSize: '0.85rem', height: '32px' }}
      />
      {children}
      {touched && ((error && <div className="invalid-feedback">{error}</div>) || (warning && <div className="text-warning small">{warning}</div>))}
    </div>
  </div>
);

const theme = createTheme({
  typography: {
    fontFamily: 'ProductSansLight, sans-serif',
    h3: {
      fontFamily: 'ProductSansBold, sans-serif',
      fontSize: 24, // Reduced font size
      fontWeight: 600,
      marginBottom: '4px'
    },
    subtitle1: {
      fontFamily: 'ProductSansLight, sans-serif',
      fontSize: 14, // Reduced font size
      color: '#64748b'
    },
    h6: {
      fontSize: 16,
    }
  },
  palette: {
    primary: {
      main: '#0ea5e9', // Sky blue
    },
    secondary: {
      main: '#64748b', // Slate
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px', // Reduced radius
          textTransform: 'none',
          fontWeight: 600,
          padding: '6px 16px', // Adjusted padding
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px', // Reduced radius
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1) !important'
        }
      }
    },
  }
});

const OrderHistoryViewIndex = ({ formValues, reset }) => {
  useEffect(() => {
    getAllRidersDetails();
    getMerchants();
  }, [])

  const [reactTableData, setReactTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [temp, setTempData] = useState("");
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [viewRemarksPopUp, setRemarksPopUp] = useState(false);
  const [isTitle, setTitle] = useState(null);
  const [isOrderIdRemarks, setOrderIdRemarks] = useState(null);
  const [riderIds, setRiderIds] = useState([]);
  const [isMerchantList, setMerchantIds] = useState([]);
  const [isTotalCount, seTotalCount] = useState(0);
  const [isPageSize, setPageSize] = useState(null);
  const [isTotalPages, setTotalPages] = useState(null);
  const [expanded, setExpanded] = useState(true); // Control accordion expansion

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };


  const getAllRidersDetails = async () => {
    let riderId = [];
    try {
      setLoading(true);
      const response = await PndRestApi.GetAllRidersDetails();
      if (response.status === 200 && response.data.status === 200) {
        response.data.data.map(item => {
          riderId.push({ "label": item.riderName, "value": item.riderId });
        })
        setRiderIds(riderId);
      } else if (response.status === 200 && response.data.status === 204) {
        setNoRecordFound(true);
      } else {
        console.error('fetching data:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchBasedOnFilters = async () => {
    try {
      let merchantId = formValues?.merchantId?.value;
      let orderFromDate = formValues?.orderFromDate;
      let orderToDate = formValues?.orderToDate;
      let orderType = formValues?.orderType?.value;
      let riderId = formValues?.riderId?.value;
      let orderStatus = formValues?.orderStatus?.value;
      let orderNo = formValues?.orderNo;

      const hasAnyFilter = Boolean(
        merchantId ||
        orderFromDate ||
        orderToDate ||
        (orderType !== undefined && orderType !== null && orderType !== '') ||
        riderId ||
        (orderStatus !== undefined && orderStatus !== null && orderStatus !== '') ||
        (orderNo && String(orderNo).trim() !== '')
      );

      if (!hasAnyFilter) {
        showNotification(
          "theme-light",
          "ltr",
          "Warning",
          "Please select at least one filter (e.g. Date Range, Order No, Rider, Status, or Merchant) to search history."
        );
        return;
      }

      if (orderFromDate != null && orderToDate != null) {
        if (orderFromDate > orderToDate) {
          showNotification("theme-light", "ltr", `SuccessCode: 500`, `Message: Order From Date should be less than Order To Date.`);
          return;
        }
      } else if (orderFromDate != null && orderToDate == null) {
        showNotification("theme-light", "ltr", `SuccessCode: 500`, `Message: Please choose Order To Date.`);
        return;
      } else if (orderFromDate == null && orderToDate != null) {
        showNotification("theme-light", "ltr", `SuccessCode: 500`, `Message: Please choose Order From Date.`);
        return;
      }

      setLoading(true);
      setNoRecordFound(false);
      setReactTableData([]);

      let Obj = {
        merchantId: merchantId,
        orderFromDate: orderFromDate,
        orderToDate: orderToDate,
        orderType: orderType,
        riderId: riderId,
        orderStatus: orderStatus,
        orderNo: orderNo ? String(orderNo).trim() : orderNo,
        isReturn: false,
      };

      const response = await PndRestApi.GetOrderHistory(Obj);
      if (response.status === 200 && response.data.status === 200) {
        setReactTableData({ tableHeaderData: columns, tableRowsData: response.data.data });
      } else if (response.status && response?.data?.status === 204) {
        setNoRecordFound(true);
        setReactTableData({ tableHeaderData: columns, tableRowsData: null });
      } else {
        showNotification("theme-light", "ltr", `SuccessCode: 500`, ERROR_MESSAGES.tryCatchError);
      }
    } catch (error) {
      console.log("error", error);
      showNotification("theme-light", "ltr", `SuccessCode: 500`, ERROR_MESSAGES.tryCatchError);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: (row, i) => i + 1,
        disableGlobalFilter: true,
        disableSortBy: true,
        width: 60,
        align: 'center',
        Cell: ({ row }) => (
          <span style={{ fontWeight: 700, color: '#64748b', fontSize: '0.8rem' }}>
            {row.index + 1}
          </span>
        ),
      },
      {
        Header: 'Action',
        accessor: 'remarksview',
        disableGlobalFilter: true,
        disableSortBy: true,
        width: 110,
        align: 'center',
        Cell: row => (
          <div className="d-flex align-items-center justify-content-center gap-1">
            <Tooltip title="View Details" arrow placement="top">
              <IconButton
                size="small"
                onClick={() => viewDetails(row.row.original)}
                sx={{
                  color: '#0ea5e9',
                  bgcolor: '#e0f2fe',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { bgcolor: '#0ea5e9', color: '#ffffff', transform: 'scale(1.08)' },
                  width: 30,
                  height: 30,
                }}
              >
                <PreviewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Remarks" arrow placement="top">
              <IconButton
                size="small"
                onClick={() => viewRemarks(row.row.original)}
                sx={{
                  color: '#64748b',
                  bgcolor: '#f1f5f9',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { bgcolor: '#64748b', color: '#ffffff', transform: 'scale(1.08)' },
                  width: 30,
                  height: 30,
                }}
              >
                <CommentIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
      {
        Header: "Order Type",
        accessor: "orderType",
        width: 120,
        align: 'center',
        Cell: ({ row }) => {
          const type = row.original.orderType;
          if (type === 1) {
            return (
              <Chip
                label="Drop Off"
                size="small"
                sx={{
                  bgcolor: '#dcfce7',
                  color: '#15803d',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  border: '1px solid #bbf7d0',
                  height: 24,
                  px: 0.5
                }}
              />
            );
          }
          if (type === 2) {
            return (
              <Chip
                label="Pick Up"
                size="small"
                sx={{
                  bgcolor: '#e0f2fe',
                  color: '#0369a1',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  border: '1px solid #bae6fd',
                  height: 24,
                  px: 0.5
                }}
              />
            );
          }
          return (
            <Chip
              label="Pick & Drop"
              size="small"
              sx={{
                bgcolor: '#fef3c7',
                color: '#b45309',
                fontWeight: 700,
                fontSize: '0.72rem',
                border: '1px solid #fde68a',
                height: 24,
                px: 0.5
              }}
            />
          );
        }
      },
      {
        Header: 'Order No.',
        accessor: 'orderNo',
        width: 150,
        align: 'left',
        Cell: ({ value }) => (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
            {value || '--'}
          </Typography>
        )
      },
      {
        Header: 'ERP Order No.',
        accessor: 'erpOrderNo',
        width: 140,
        align: 'left',
        Cell: ({ value }) => (
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.82rem' }}>
            {value || '--'}
          </Typography>
        )
      },
      {
        Header: 'Requested Date',
        accessor: 'requestedDateTimeSlot',
        width: 170,
        align: 'left',
        Cell: ({ value }) => (
          <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.82rem', fontWeight: 500 }}>
            {value ? safeFormatDateOnly(value) : '—'}
          </Typography>
        )
      },
      {
        Header: 'Time Slot',
        accessor: 'requestedTimeSlot',
        width: 140,
        align: 'center',
        Cell: ({ value }) => (
          value ? (
            <Chip
              label={value}
              size="small"
              variant="outlined"
              sx={{
                bgcolor: '#f8fafc',
                borderColor: '#cbd5e1',
                color: '#475569',
                fontSize: '0.72rem',
                fontWeight: 600,
                height: 24
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>--</Typography>
          )
        )
      },
      {
        Header: 'Area',
        accessor: 'areaName',
        width: 150,
        align: 'left',
        Cell: ({ value }) => (
          <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, fontSize: '0.82rem' }}>
            {value || '--'}
          </Typography>
        )
      },
      {
        Header: "Order Status",
        accessor: "orderStatusInfo.orderStatusDes",
        width: 220,
        align: 'center',
        Cell: ({ row }) => {
          const statusDes = row.original?.orderStatusInfo?.orderStatusDes || 'Unknown';

          let chipBg = '#f1f5f9';
          let chipColor = '#334155';
          let chipBorder = '#cbd5e1';

          const desLower = statusDes.toLowerCase();
          if (desLower.includes('delivered') && !desLower.includes('undelivered') && !desLower.includes('part')) {
            chipBg = '#dcfce7';
            chipColor = '#15803d';
            chipBorder = '#bbf7d0';
          } else if (desLower.includes('part delivered')) {
            chipBg = '#e0f2fe';
            chipColor = '#0369a1';
            chipBorder = '#bae6fd';
          } else if (desLower.includes('undelivered') || desLower.includes('cancel')) {
            chipBg = '#ffe4e6';
            chipColor = '#9f1239';
            chipBorder = '#fecdd3';
          } else if (desLower.includes('assign') || desLower.includes('transit')) {
            chipBg = '#fef3c7';
            chipColor = '#b45309';
            chipBorder = '#fde68a';
          }

          if (row.original.rescheduledStatus === 1) {
            return (
              <Tooltip title="Reschedule request came from Rider, once the rider is scanned out in the hub it will be available in the Reschedule page in order management" arrow placement="top">
                <Chip
                  label={`${statusDes} - RSR`}
                  size="small"
                  sx={{
                    bgcolor: '#ffe4e6',
                    color: '#e11d48',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    border: '1px solid #fecdd3',
                    cursor: 'pointer',
                    height: 24
                  }}
                />
              </Tooltip>
            );
          }

          let labelText = statusDes;
          if (row.original.returnedStatus) {
            labelText = `${statusDes} - Requested for RO`;
            chipBg = '#fff1f2';
            chipColor = '#be123c';
            chipBorder = '#fecdd3';
          } else if (row.original.rescheduledStatus === 2) {
            labelText = `${statusDes} - Requested for RS`;
            chipBg = '#fef3c7';
            chipColor = '#b45309';
            chipBorder = '#fde68a';
          }

          return (
            <Chip
              label={labelText}
              size="small"
              sx={{
                bgcolor: chipBg,
                color: chipColor,
                fontWeight: 600,
                fontSize: '0.72rem',
                border: `1px solid ${chipBorder}`,
                height: 24
              }}
            />
          );
        },
      },
    ],
    [],
  );

  const viewDetails = async (event) => {
    if (event?.orderId) {
      setTempData(event);
      setViewDetailPopup(true);
      setTitle("View Order Details")
    }
  }

  const viewRemarks = async (event) => {
    if (event?.orderId) {
      setOrderIdRemarks(event?.orderId);
      setRemarksPopUp(true);
      setTitle("Order Remarks")
    }
  }

  const clearFilters = () => {
    reset();
  };

  const getMerchants = async () => {
    let merchantIds = []
    try {
      const response = await PndRestApi.GetAllMerchantHubMappingDetails();
      if (response.status === 200 && response.data.status === 200) {
        response.data.data.map(item => {
          merchantIds.push({ "label": item.merchantName, "value": item.merchantId });
        })
        setMerchantIds(merchantIds);
      } else {
        console.error('Unexpected response status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="p-3" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>

        {/* Header Section */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <Typography variant="h3" sx={{ color: '#1e293b' }}>Order History</Typography>
            <Typography variant="subtitle1">View and manage all your order history records.</Typography>
          </div>
        </Box>

        {/* Filters Section - Compact */}
        <Accordion
          expanded={expanded}
          onChange={handleAccordionChange}
          sx={{ mb: 2, borderRadius: '12px !important', '&:before': { display: 'none' }, border: '1px solid #e2e8f0', boxShadow: 'none' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            sx={{
              minHeight: '48px',
              '& .MuiAccordionSummary-content': { margin: '12px 0' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ color: '#0ea5e9', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#334155' }}>Search Filters</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3} lg={2}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order No.</label>
                    <Field
                      name="orderNo"
                      component={renderCompactField}
                      type="text"
                      placeholder="Type Order No."
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order From Date</label>
                    <Field
                      name="orderFromDate"
                      component={renderMuiDateTimePickerField}
                      fullWidth
                      size="small"
                      margin="none"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order To Date</label>
                    <Field
                      name="orderToDate"
                      component={renderMuiDateTimePickerField}
                      fullWidth
                      size="small"
                      margin="none"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order Type</label>
                    <Field
                      name="orderType"
                      component={renderSelectField}
                      options={[
                        { value: 0, label: "All" },
                        { value: 1, label: "Drop Off" },
                        { value: 2, label: "Pick Up" },
                        { value: 3, label: "Pick & Drop" },
                      ]}
                      placeholder="Select Type"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Status</label>
                    <Field
                      name="orderStatus"
                      component={renderSelectField}
                      options={[
                        { value: 0, label: "All" },
                        { value: 3, label: "Full Delivered" },
                        { value: 9, label: "Part Delivered" },
                        { value: 5, label: "Undelivered" },
                        { value: 4, label: "Cancelled" },
                      ]}
                      placeholder="Select Status"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Driver</label>
                    <Field
                      name="riderId"
                      component={renderSelectField}
                      options={riderIds}
                      placeholder="Select Driver"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Merchant</label>
                    <Field
                      name="merchantId"
                      component={renderSelectField}
                      options={isMerchantList}
                      placeholder="Select Merchant"
                    />
                  </div>
                </Grid>

                <Grid item xs={12} md={9} lg={10} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={clearFilters}
                    startIcon={<ClearAllIcon fontSize="small" />}
                    size="small"
                    sx={{ borderColor: '#cbd5e1', color: '#64748b' }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={searchBasedOnFilters}
                    startIcon={<SearchIcon fontSize="small" />}
                    size="small"
                    sx={{ color: '#fff' }}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </form>
          </AccordionDetails>
        </Accordion>


        {/* Results Section */}
        <Box sx={{ mt: 1 }}>
          {loading && <LinearProgress sx={{ borderRadius: 4, mb: 1 }} />}

          {isNoRecordFound && (
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px dashed #cbd5e1', bgcolor: '#f8fafc' }}>
              <Typography variant="body2" color="textSecondary">No records found matching your criteria.</Typography>
            </Paper>
          )}

          {(reactTableData && reactTableData?.tableRowsData) && (
            <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <OrderHistoryView reactTableData={reactTableData} isTotalCount={isTotalCount} isPageSize={isPageSize} isTotalPages={isTotalPages} />
            </Paper>
          )}
        </Box>

        {viewDetailPopup && (
          <OrderDetailsShippingViewDetailsModal
            modalShow={viewDetailPopup}
            setModalShow={setViewDetailPopup}
            dataInfo={temp}
            isTitle={isTitle}
          />
        )}
        {viewRemarksPopUp && (
          <OrderRemarksDetailsModel
            modalShow={viewRemarksPopUp}
            isOrderId={isOrderIdRemarks}
            setModalShow={setRemarksPopUp}
            isTitle={isTitle}
          />
        )}
      </div>
    </ThemeProvider>
  );
};

const mapStateToProps = (state) => ({
  formValues: state.form.OrderHistoryViewIndexForm ? state.form.OrderHistoryViewIndexForm.values : {},
});

export default connect(mapStateToProps)(reduxForm({ form: 'OrderHistoryViewIndexForm', enableReinitialize: true })(OrderHistoryViewIndex));